import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, UserSwitch, CheckCircle, Warning, CalendarBlank,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import useDirectory from '@/hooks/useDirectory';
import { useAuth } from '@/auth/AuthContext';
import ShizaiDelegationTimelineCard from './ShizaiDelegationTimelineCard';
import { todayJst } from '@/utils/jstDate';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const NONE = '__none__';

const todayIso = () => todayJst();
const isActiveWindow = (d) => {
  const t = todayIso();
  return !!d.active && d.from_date <= t && d.to_date >= t;
};

const emptyForm = () => ({
  approver_id: '', approver_email: '',
  delegate_id: '', delegate_email: '',
  from_date: todayIso(), to_date: '',
  note: '', active: true,
});

/** Admin sees ALL delegations; regular users see only their own. Both can
 *  create, edit and delete their own delegations. Admin can manage others'. */
export default function ShizaiDelegationsSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const { employees } = useDirectory();

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_ROOT}/shizai/delegations`, {
      params: { mine_only: !isAdmin || mineOnly },
    })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [isAdmin, mineOnly]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    const f = emptyForm();
    // Pre-fill "I am delegating myself" when the user opens the dialog
    if (!isAdmin) {
      f.approver_id = user?.id || '';
      f.approver_email = user?.email || '';
    }
    setForm(f);
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      approver_id: d.approver_id || '',
      approver_email: d.approver_email || '',
      delegate_id: d.delegate_id || '',
      delegate_email: d.delegate_email || '',
      from_date: d.from_date, to_date: d.to_date,
      note: d.note || '', active: !!d.active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.approver_email.trim() || !form.delegate_email.trim()) {
      toast.error('承認者と委任先のメールアドレスを入力してください'); return;
    }
    if (!form.from_date || !form.to_date) {
      toast.error('期間を指定してください'); return;
    }
    if (form.from_date > form.to_date) {
      toast.error('開始日は終了日以前を指定してください'); return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        approver_id: form.approver_id || form.approver_email,  // API requires min_length=1
        delegate_id: form.delegate_id || null,
      };
      if (editing) {
        await axios.put(`${API_ROOT}/shizai/delegations/${editing.id}`, body);
        toast.success('委任を更新しました');
      } else {
        await axios.post(`${API_ROOT}/shizai/delegations`, body);
        toast.success('委任を作成しました');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  const del = async (d) => {
    if (!window.confirm(`${d.approver_email} → ${d.delegate_email} の委任を削除しますか？`)) return;
    try {
      await axios.delete(`${API_ROOT}/shizai/delegations/${d.id}`);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API_ROOT}/shizai/delegations/${d.id}`,
        { ...d, active: !d.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '状態変更に失敗しました');
    }
  };

  const activeCount = useMemo(() => rows.filter(isActiveWindow).length, [rows]);

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-delegations-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <UserSwitch size={22} weight="duotone" className="text-orange-500" />
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">承認委任</h1>
          <span className="text-xs text-zinc-500 tabular-nums">({rows.length}件・稼働中 {activeCount})</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <label className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)}
                data-testid="dg-mine-only" />
              自分の委任のみ
            </label>
          )}
          <Button onClick={openNew} data-testid="dg-add" className="h-10 bg-teal-700 hover:bg-teal-800">
            <Plus size={14} className="mr-1" weight="bold" />委任を追加
          </Button>
        </div>
      </div>

      <div className="text-xs text-zinc-500 bg-orange-50 border border-orange-200 rounded-lg p-3">
        💡 <b>期間中は承認依頼が委任先に自動で転送されます</b>。休暇や出張時にご利用ください。<br />
        滞留日数を超えた承認は、まず委任先へ、次にルールの代理承認者へエスカレートします。
      </div>

      {/* iter_143e · P2/v4 delegation timeline — visual view over next 60 days */}
      <ShizaiDelegationTimelineCard refreshKey={rows.length} />

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-400">読み込み中...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-12 text-center" data-testid="dg-empty">
          <UserSwitch size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">委任がまだ設定されていません</p>
          <p className="text-xs text-zinc-400 mt-1">休暇中の承認を別の担当者に自動転送するには「委任を追加」から設定してください</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">状態</th>
                <th className="text-left px-3 py-2 font-semibold">承認者（From）</th>
                <th className="text-left px-3 py-2 font-semibold">委任先（To）</th>
                <th className="text-left px-3 py-2 font-semibold">期間</th>
                <th className="text-left px-3 py-2 font-semibold">メモ</th>
                <th className="text-center px-3 py-2 font-semibold">有効</th>
                <th className="text-right px-3 py-2 font-semibold">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rows.map((d) => {
                const isNow = isActiveWindow(d);
                return (
                  <tr key={d.id} data-testid={`dg-row-${d.id}`} className={d.active ? '' : 'bg-zinc-50/60 opacity-60'}>
                    <td className="px-3 py-2">
                      {isNow ? (
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px]">
                          <CheckCircle size={10} weight="fill" className="mr-0.5 inline" />稼働中
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-zinc-500">
                          {d.to_date < todayIso() ? '終了' : '予約'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-zinc-800">{d.approver_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-orange-700">→ {d.delegate_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-600 tabular-nums">
                      <div className="flex items-center gap-1">
                        <CalendarBlank size={11} className="text-zinc-400" />
                        {d.from_date} 〜 {d.to_date}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-500 max-w-[220px] truncate" title={d.note}>{d.note || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <Switch checked={!!d.active} onCheckedChange={() => toggleActive(d)}
                        data-testid={`dg-toggle-${d.id}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)}
                        data-testid={`dg-edit-${d.id}`} className="h-8 px-2">
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(d)}
                        data-testid={`dg-del-${d.id}`} className="h-8 px-2 text-red-600 hover:bg-red-50">
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dg-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? '委任を編集' : '委任を追加'}</DialogTitle>
            <DialogDescription>
              指定した期間、承認依頼を別の担当者へ自動で回送します。
              {!isAdmin && ' 自分の承認のみ委任できます。'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  承認者（From）<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Select value={form.approver_id || (form.approver_email ? form.approver_email : NONE)}
                  onValueChange={(v) => {
                    if (v === NONE) { setForm({ ...form, approver_id: '', approver_email: '' }); return; }
                    const emp = employees.find((e) => e.id === v);
                    setForm({ ...form, approver_id: v, approver_email: emp?.email || '' });
                  }}
                  disabled={!isAdmin && !!editing}>
                  <SelectTrigger data-testid="dg-form-approver" className="h-10 mt-1"><SelectValue placeholder="選択" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>選択なし</SelectItem>
                    {employees.filter((e) => e.email).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}（{e.email}）</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={form.approver_email}
                  onChange={(e) => setForm({ ...form, approver_email: e.target.value })}
                  placeholder="approver@example.com"
                  data-testid="dg-form-approver-email"
                  className="h-9 mt-2 text-xs"
                  disabled={!isAdmin && !!editing} />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  委任先（To）<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Select value={form.delegate_id || (form.delegate_email ? form.delegate_email : NONE)}
                  onValueChange={(v) => {
                    if (v === NONE) { setForm({ ...form, delegate_id: '', delegate_email: '' }); return; }
                    const emp = employees.find((e) => e.id === v);
                    setForm({ ...form, delegate_id: v, delegate_email: emp?.email || '' });
                  }}>
                  <SelectTrigger data-testid="dg-form-delegate" className="h-10 mt-1"><SelectValue placeholder="選択" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>選択なし</SelectItem>
                    {employees.filter((e) => e.email).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}（{e.email}）</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={form.delegate_email}
                  onChange={(e) => setForm({ ...form, delegate_email: e.target.value })}
                  placeholder="delegate@example.com"
                  data-testid="dg-form-delegate-email"
                  className="h-9 mt-2 text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">開始日<span className="text-red-500 ml-0.5">*</span></Label>
                <Input type="date" value={form.from_date}
                  onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                  data-testid="dg-form-from"
                  className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">終了日<span className="text-red-500 ml-0.5">*</span></Label>
                <Input type="date" value={form.to_date}
                  onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                  data-testid="dg-form-to"
                  className="h-10 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">メモ</Label>
              <Input value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="夏季休暇 / 出張 / 産休 など"
                data-testid="dg-form-note"
                className="h-10 mt-1" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })}
                data-testid="dg-form-active" />
              <span className="text-sm text-zinc-700">有効</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={busy}
              data-testid="dg-form-save"
              className="bg-teal-700 hover:bg-teal-800">
              {busy ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
